import {
  BOSS_APPEARANCE_DENOMINATOR,
  BOSS_DELAY_MAX_MS,
  BOSS_DELAY_MIN_MS,
  BOSS_TIERS,
  type BossTier,
  bossTierDefinition,
} from './tier';

/**
 * A boss encounter: which boss is coming, when, and what happened to it.
 *
 * ## Why an encounter exists at all
 *
 * The boss used to be a countdown inside the simulation and nothing else. Its
 * size came from the SIMULATION stream, whose seed is public and is re-minted
 * by a death or an F5 - so a player could reload until the boss they were about
 * to fight looked good. That was tolerable while size was cosmetic. It stops
 * being tolerable the moment size decides Gold and drop odds.
 *
 * So the encounter becomes a persisted, server-drawn fact: a tier and a delay,
 * chosen once with cryptographic entropy, written down before they can affect
 * anything, and never drawn again for the same encounter. A reload finds the
 * row that is already there.
 *
 * ## What is in this module and what is not
 *
 * Pure decisions only: how a tier is picked from a uniform draw, how a delay is
 * picked from a uniform draw, and what the states mean. Where the entropy comes
 * from belongs to the server, and where the row lives belongs to the database.
 * Nothing here can reach either.
 */

/** What an encounter is doing right now. */
export const BOSS_ENCOUNTER_STATES = [
  /** Drawn and written, counting down. The player cannot see the tier yet. */
  'scheduled',
  /** On the field, being fought. */
  'active',
  /**
   * The same boss, waiting out a recovery window after it killed the player.
   *
   * Not a second encounter and not a re-draw: the id, the ordinal and the tier
   * are untouched. It exists because a boss that came straight back trapped a
   * ship too weak to beat it - it could never earn the levels that would let it
   * win, which is a soft-lock reachable by playing normally.
   */
  'retry_wait',
  /** Confirmed dead. Terminal - a defeated encounter is never reopened. */
  'defeated',
] as const;

export type BossEncounterState = (typeof BOSS_ENCOUNTER_STATES)[number];

/** The things worth writing down permanently about an encounter. */
export const BOSS_ENCOUNTER_EVENTS = [
  'scheduled',
  'activated',
  /** The player died while it was on the field. It is still the same boss. */
  'attempt_interrupted',
  'defeated',
] as const;

export type BossEncounterEvent = (typeof BOSS_ENCOUNTER_EVENTS)[number];

/**
 * How an encounter came to exist.
 *
 * `adopted` is the interesting one: a hunt that was already running when
 * encounters did not exist yet keeps its countdown and is given the tier it
 * never had, rather than being restarted underneath the player.
 */
export const BOSS_ENCOUNTER_ORIGINS = ['first', 'after_defeat', 'adopted'] as const;

export type BossEncounterOrigin = (typeof BOSS_ENCOUNTER_ORIGINS)[number];

/* -------------------------------------------------------------------------- */
/*  The tier draw                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Picks a tier from a uniform integer in `[0, BOSS_APPEARANCE_DENOMINATOR)`.
 *
 * A cumulative walk over the SAME `appearanceParts` the tier table already
 * publishes - not a second copy of the distribution. Because the parts sum to
 * exactly the denominator (asserted by equality in the tier's own tests), every
 * draw lands in exactly one band and the last band needs no special case.
 *
 * The caller owes a draw that is uniform and unbiased. Modulo of a wider
 * generator is not: at this denominator it would make the LOW tiers measurably
 * more common, which is to say it would quietly make every boss in the game
 * worth less Gold than the published table says.
 */
export function tierForDraw(draw: number): BossTier {
  if (!Number.isSafeInteger(draw) || draw < 0 || draw >= BOSS_APPEARANCE_DENOMINATOR) {
    throw new RangeError(
      `boss tier draw must be an integer in [0, ${BOSS_APPEARANCE_DENOMINATOR}), got ${draw}`,
    );
  }
  let cursor = 0;
  for (const tier of BOSS_TIERS) {
    cursor += bossTierDefinition(tier).appearanceParts;
    if (draw < cursor) {
      return tier;
    }
  }
  // Unreachable while the parts total the denominator. Falling through would
  // mean the table had been edited without its test noticing, so it refuses
  // rather than quietly handing out tier 6.
  throw new RangeError(`boss appearance parts do not cover ${draw}`);
}

/** The draw space a tier is picked from. The caller draws below this. */
export const BOSS_TIER_DRAW_SPACE = BOSS_APPEARANCE_DENOMINATOR;

/* -------------------------------------------------------------------------- */
/*  The delay draw                                                            */
/* -------------------------------------------------------------------------- */

/** Whole seconds, so the draw space is small and exactly enumerable. */
export const BOSS_DELAY_MIN_SECONDS = BOSS_DELAY_MIN_MS / 1000;
export const BOSS_DELAY_MAX_SECONDS = BOSS_DELAY_MAX_MS / 1000;

/**
 * How many distinct delays exist: 180..960 inclusive, so 781 of them.
 *
 * Inclusive at BOTH ends deliberately. A half-open range would make 960 s
 * unreachable, and "up to sixteen minutes" would be a bound the game never
 * actually reached.
 */
export const BOSS_DELAY_DRAW_SPACE = BOSS_DELAY_MAX_SECONDS - BOSS_DELAY_MIN_SECONDS + 1;

/**
 * Turns a uniform draw in `[0, BOSS_DELAY_DRAW_SPACE)` into milliseconds.
 *
 * Integers throughout: seconds are added, then multiplied by 1000. Picking a
 * float in the interval and rounding would concentrate the two endpoints at
 * half the weight of every other value, which is a bias nobody would ever see
 * and everybody would inherit.
 */
export function delayMsForDraw(draw: number): number {
  if (!Number.isSafeInteger(draw) || draw < 0 || draw >= BOSS_DELAY_DRAW_SPACE) {
    throw new RangeError(
      `boss delay draw must be an integer in [0, ${BOSS_DELAY_DRAW_SPACE}), got ${draw}`,
    );
  }
  return (BOSS_DELAY_MIN_SECONDS + draw) * 1000;
}

/* -------------------------------------------------------------------------- */
/*  What the client is allowed to know                                        */
/* -------------------------------------------------------------------------- */

/**
 * What the browser may be told about a SCHEDULED encounter.
 *
 * Enough to draw a countdown and nothing else. The tier is absent by
 * construction rather than by omission - there is no field to forget to strip -
 * because advance notice of what the next boss is worth is an invitation to
 * stop playing until a good one comes up. The visual scale is absent for the
 * same reason: it is the tier, expressed in pixels.
 *
 * `remainingMs` is the SERVER's authoritative countdown at the instant it
 * answered. The client counts down from it; it never invents one, and it never
 * decides that it reached zero.
 */
export interface ScheduledBossEncounter {
  readonly state: 'scheduled';
  /** Opaque and stable. Enough to correlate an answer with a countdown. */
  readonly encounterId: string;
  /** Milliseconds of active hunting still owed before the boss arrives. */
  readonly remainingMs: number;
  readonly mapIndex: number;
}

/**
 * What the browser may be told once the boss is ON THE FIELD.
 *
 * The tier appears here and nowhere earlier. By the time this is sent the
 * server has already re-simulated the activation, so the number is a fact
 * about a boss the player can see rather than a prediction they could act on.
 */
export interface ActiveBossEncounter {
  readonly state: 'active';
  readonly encounterId: string;
  readonly tier: BossTier;
  /** The map's mob scale times the tier's, in basis points. */
  readonly visualScaleBps: string;
  readonly mapIndex: number;
}

/**
 * The encounter as the wire carries it.
 *
 * A discriminated union rather than one shape with optional fields, so a
 * scheduled encounter cannot accidentally carry a tier: the type has no room
 * for one. A defeated encounter is never sent - by the time a defeat is
 * answered, its successor already exists and IS the current encounter.
 */
export type PublicBossEncounter = ScheduledBossEncounter | ActiveBossEncounter;

/**
 * Whether an encounter may be described to the client at all.
 *
 * One function rather than a check at each call site: "is it safe to send this"
 * is the kind of question that gets answered correctly in three places and
 * forgotten in the fourth.
 */
export function encounterIsPublic(state: BossEncounterState): boolean {
  return state === 'active';
}

/** True while the encounter is counting down rather than being fought. */
export function encounterIsCountingDown(state: BossEncounterState): boolean {
  return state === 'scheduled' || state === 'retry_wait';
}
